/* eslint-disable react/prop-types */
/* eslint-disable react/no-unstable-nested-components */
import {
  forwardRef, useMemo, useRef, useEffect, useState,
} from 'react';
import { useTable, useRowSelect } from 'react-table';
import PropTypes from 'prop-types';
import { useQueryClient } from 'react-query';
import { Modal } from 'antd';
import resultIsSuccess from '../../utils/resultIsSuccess';
import translateResult from '../../utils/translateResult';
import {
  ResultTableData, Table, IconButton, Row, Pre, MarginTop,
} from './styles';
import httpClient from '../../datasources/httpClient';
import useAuth from '../../hooks/useAuth';
import { formatRelativeBr } from '../../utils/format';
import Loader from '../Loader';
import BackupForm from '../BackupForm';

const IndeterminateCheckbox = forwardRef(
  // eslint-disable-next-line react/prop-types
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <input type="checkbox" ref={resolvedRef} {...rest} />
    );
  },
);

export default function BackupsTable({ backups, members }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [openConfig, setOpenConfig] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalContent, setModalContent] = useState('');

  const columns = useMemo(() => [
    {
      Header: 'Nome',
      accessor: 'name',
      Cell: ({ row, value }) => (<a href={`/reports/${row.id}`}>{value}</a>),
    },
    { Header: 'Tamanho', accessor: 'size' },
    { Header: 'Próximo relatório', accessor: 'nextReportExpectedAt' },
    { Header: 'Último Backup Bem-Sucedido', accessor: 'lastSuccefulReport' },
    { Header: 'Último Relatório', accessor: 'lastReport' },
  ], []);

  const data = useMemo(() => backups.map((backup) => {
    if (backup.reports.length === 0) {
      return ({
        name: backup.name,
        id: backup.id,
      });
    }

    const lastSuccefulReport = backup.reports
      .find((report) => resultIsSuccess({ value: report.result }));

    const lastReport = backup.reports[0];
    const lastReportResult = translateResult(lastReport.result);
    const now = Date.now();

    return ({
      name: backup.name,
      size: `${(parseInt(lastReport.size, 10) / (1024 * 1024)).toFixed(2)} MB`,
      nextReportExpectedAt: formatRelativeBr(new Date(backup.nextReportExpectedAt), now),
      lastSuccefulReport: lastSuccefulReport ? `${formatRelativeBr(new Date(lastSuccefulReport.date), now)}` : null,
      lastReport: `${lastReportResult} ${formatRelativeBr(new Date(lastReport.date), now)}`,
      id: backup.id,
    });
  }), [backups]);

  const getRowId = (row, relativeIndex, parent) => (parent
    ? [parent.id, row.id].join('.') : row.id);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { selectedRowIds },
  } = useTable(
    { columns, data, getRowId },
    useRowSelect,
    (hooks) => {
      const noSelection = () => {
        hooks.visibleColumns.push((visibleColumns) => [
          {
            id: 'selection',
            Header: () => null,
            Cell: () => (
              <div />
            ),
          },
          ...visibleColumns,
        ]);
      };
      const userRole = members.find((member) => member.email === user.email)?.role;

      if (!userRole || userRole.name !== 'admin') {
        return noSelection();
      }

      return hooks.visibleColumns.push((visibleColumns) => [
        {
          id: 'selection',
          Header: () => null,
          Cell: ({ row }) => (
            <div>
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            </div>
          ),
        },
        ...visibleColumns,
      ]);
    },
  );

  async function handleBackupDeletion() {
    const ids = Object.keys(selectedRowIds);
    await httpClient.delete('backups', {
      data: { ids },
    });

    queryClient.invalidateQueries('backups');
  }

  async function handleFetchBackupConfiguration() {
    const ids = Object.keys(selectedRowIds);

    if (ids.length === 1) {
      try {
        setLoading(true);
        setOpenConfig(true);
        const response = await httpClient.get(`backups-config/${ids[0]}`);

        if (response.status === 200) {
          setModalContent(response.data);
        }
      } catch (error) {
        setOpenConfig(false);
      } finally {
        setLoading(false);
      }
    }
  }

  function handleClose() {
    setOpenConfig(false);
    setOpenDelete(false);
    setOpenEdit(false);
  }

  function handleCopy() {
    setOpenConfig(false);
    navigator.clipboard.writeText(JSON.stringify(modalContent));
  }

  return (
    <>
      {
        Object.keys(selectedRowIds).length >= 1
          ? (
            <Row>
              {
                Object.keys(selectedRowIds).length === 1 ? (
                  <>
                    <IconButton
                      title="Edit row"
                      type="button"
                      onClick={() => setOpenEdit(true)}
                    >
                      <span className="material-symbols-outlined grey">
                        edit
                      </span>
                    </IconButton>
                    <Modal
                      centered
                      open={openEdit}
                      footer={[]}
                      onCancel={() => handleClose()}

                    >
                      <MarginTop>
                        <BackupForm backupId={Object.keys(selectedRowIds)[0]} />
                      </MarginTop>
                    </Modal>
                  </>
                ) : null
              }
              {Object.keys(selectedRowIds).length === 1 ? (
                <div>
                  <IconButton
                    title="Get selected rows config"
                    type="button"
                    onClick={() => handleFetchBackupConfiguration()}
                  >
                    <span className="material-symbols-outlined grey">
                      settings
                    </span>
                  </IconButton>
                  <Modal
                    centered
                    open={openConfig}
                    onCancel={() => handleClose()}
                    onOk={() => handleCopy()}
                    okText="Copiar"
                    cancelText="Cancelar"
                  >
                    {
                      loading
                        ? <Loader />
                        : (
                          <Pre data-lang="JSON">
                            <code>
                              {JSON.stringify(modalContent)}
                            </code>
                          </Pre>
                        )
                    }
                  </Modal>
                </div>
              ) : null}
              <div>
                <IconButton
                  title="Delete selected rows"
                  type="button"
                  onClick={() => setOpenDelete(true)}
                >
                  <span className="material-symbols-outlined">
                    delete
                  </span>
                </IconButton>
                <Modal
                  centered
                  open={openDelete}
                  onCancel={() => handleClose()}
                  onOk={() => handleBackupDeletion()}
                  title="Confirmação de exclusão"
                  okText="Deletar"
                  cancelText="Cancelar"
                  okType="danger"
                >
                  Tem certeza que deseja deletar o(s) backup(s) selecionado(s)?
                </Modal>
              </div>
            </Row>
          )
          : null
      }
      <Table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (<th {...column.getHeaderProps()}>{column.render('Header')}</th>))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);

            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  if (cell.column.Header === 'Último Relatório') {
                    const isSuccess = cell.value?.includes('Bem-sucedido');
                    const isWarning = cell.value?.includes('Alerta');

                    return (
                      <ResultTableData
                        isSuccess={isSuccess}
                        isWarning={isWarning}
                        {...cell.getCellProps()}
                      >
                        {cell.render('Cell')}
                      </ResultTableData>
                    );
                  }

                  return (<td {...cell.getCellProps()}>{cell.render('Cell')}</td>);
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

BackupsTable.propTypes = {
  backups: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    reports: PropTypes.arrayOf(PropTypes.shape({
      result: PropTypes.string.isRequired,
      size: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
    })),
    company: PropTypes.shape({
      cnpj: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  })).isRequired,
  members: PropTypes.arrayOf(PropTypes.shape({
    email: PropTypes.string.isRequired,
  })).isRequired,
};
